import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Palette,
  Film,
  GraduationCap,
  ArrowRight,
  Users,
  ImagePlus,
  Plus,
  Pencil,
  Trash2,
  Music,
  Loader2,
  RefreshCw,
  Mic,
  Save,
  ChevronLeft,
  ChevronRight,
  Library,
  LayoutGrid,
} from 'lucide-react';
import { STORY_FLOWS } from '@/data/storyFlows';
import { CinematicBlueprintEditor } from '@/components/CinematicBlueprintEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { durationMinutes, nearestValue, pickOption, readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/** Runway Live POST /v1/avatars — presetId values (lowercase). */
const RUNWAY_LIVE_AVATAR_PRESETS = [
  { value: 'victoria', label: 'Victoria' },
  { value: 'vincent', label: 'Vincent' },
  { value: 'clara', label: 'Clara' },
  { value: 'drew', label: 'Drew' },
  { value: 'skye', label: 'Skye' },
  { value: 'max', label: 'Max' },
  { value: 'morgan', label: 'Morgan' },
  { value: 'felix', label: 'Felix' },
  { value: 'mia', label: 'Mia' },
  { value: 'marcus', label: 'Marcus' },
  { value: 'summer', label: 'Summer' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'aurora', label: 'Aurora' },
  { value: 'jasper', label: 'Jasper' },
  { value: 'leo', label: 'Leo' },
  { value: 'adrian', label: 'Adrian' },
  { value: 'nina', label: 'Nina' },
  { value: 'emma', label: 'Emma' },
  { value: 'blake', label: 'Blake' },
  { value: 'david', label: 'David' },
  { value: 'maya', label: 'Maya' },
  { value: 'nathan', label: 'Nathan' },
  { value: 'sam', label: 'Sam' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'petra', label: 'Petra' },
  { value: 'adam', label: 'Adam' },
  { value: 'zach', label: 'Zach' },
  { value: 'violet', label: 'Violet' },
  { value: 'roman', label: 'Roman' },
  { value: 'luna', label: 'Luna' },
];

const CHARACTER_REFERENCE_GRID = [
  { key: 'faceFront', label: 'Face • front' },
  { key: 'faceBack', label: 'Face • back' },
  { key: 'faceLeft', label: 'Face • left' },
  { key: 'faceRight', label: 'Face • right' },
  { key: 'bodyFront', label: 'Body • front' },
  { key: 'bodyBack', label: 'Body • back' },
  { key: 'bodyLeft', label: 'Body • left' },
  { key: 'bodyRight', label: 'Body • right' },
];

function parseCreativeProfileReferenceFrames(jsonStr) {
  try {
    const o = JSON.parse(jsonStr.trim() ? jsonStr : '{}');
    const rf = o?.character?.referenceFrames;
    return rf && typeof rf === 'object' ? rf : {};
  } catch {
    return {};
  }
}

function parseCreativeProfileReferenceGeneration(jsonStr) {
  try {
    const o = JSON.parse(jsonStr.trim() ? jsonStr : '{}');
    const rg = o?.character?.referenceGeneration;
    return rg && typeof rg === 'object' ? rg : null;
  } catch {
    return null;
  }
}

/** Backend detail may be a string (HTTPException) or a validation array */
function formatApiError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (Array.isArray(d) && d.length) {
    const parts = d.map((x) =>
      typeof x === 'object' && x !== null ? x.msg || x.message || JSON.stringify(x) : String(x)
    );
    const joined = parts.filter(Boolean).join(' ');
    return joined || fallback;
  }
  if (err?.message && typeof err.message === 'string') return err.message;
  return fallback;
}

/** Default nested creative sheet when none exists (editable JSON in Story Setup popup). */
function defaultCreativeProfileFromCharacter(c) {
  const slug = String(c?.name || 'character').replace(/\s+/g, '_').toLowerCase().replace(/[^\w_-]/g, '').slice(0, 48);
  return {
    character: {
      id: c?.id ? `char_${c.id.slice(0, 8)}` : `char_${slug}`,
      name: String(c?.name || ''),
      role: String(c?.role || 'friend').replace(/_/g, ' ') || 'friend',
      description: {
        summary: String(c?.description || ''),
        physicalAppearance: {
          age: null,
          gender: String(c?.gender || 'not_specified'),
          ethnicity: '',
          height: '',
          bodyType: '',
          skinTone: '',
          faceShape: '',
          eyes: { color: '', shape: '', details: '' },
          hair: { style: '', color: '', movement: '' },
          distinctFeatures: [],
        },
        expressions: {
          default: '',
          happy: '',
          angry: '',
          sad: '',
          combat: '',
        },
      },
      clothing: {
        primaryOutfit: { top: '', bottom: '', footwear: '', materials: [], accessories: [] },
        alternateOutfits: [],
      },
      personality: {
        traits: Array.isArray(c?.personality_traits) ? [...c.personality_traits] : [],
        motivations: [],
        fears: [],
        behaviorStyle: '',
        speakingStyle: String(c?.speaking_style || ''),
      },
      voice: {
        voiceType: String(c?.voice_style || ''),
        tone: '',
        accent: '',
        pitch: '',
        delivery: '',
      },
      runwayGeneration: {
        model: 'gen-4',
        imagePrompt: '',
        videoPrompt: '',
        negativePrompt: ['low quality', 'deformed', 'blurry', 'bad anatomy'],
        styleTags: ['cinematic', 'consistent character'],
        cameraSettings: {
          lens: '35mm cinematic',
          cameraMovement: 'slow push-in',
          depthOfField: 'shallow',
          frameRate: '24fps',
          aspectRatio: '16:9',
        },
        lighting: { keyLight: '', rimLight: '', atmosphere: '' },
      },
      consistencyPrompts: {
        faceLockPrompt: '',
        outfitConsistency: '',
        bodyConsistency: '',
        environmentConsistency: '',
      },
      referenceGeneration: {
        seed: null,
        model: '',
        updatedAt: '',
        lastBulkGeneratedAt: '',
        byAngleGeneratedAt: {},
      },
      referenceFrames: {
        frontPortrait: String(c?.reference_image || ''),
        sidePortrait: '',
        fullBody: '',
        expressionSheet: '',
        faceFront: '',
        faceBack: '',
        faceLeft: '',
        faceRight: '',
        bodyFront: '',
        bodyBack: '',
        bodyLeft: '',
        bodyRight: '',
      },
    },
  };
}

function mergePortraitUrlIntoCreativeJson(jsonStr, url) {
  if (!url) return jsonStr;
  try {
    const o = jsonStr.trim() ? JSON.parse(jsonStr) : {};
    if (!o.character || typeof o.character !== 'object') o.character = {};
    if (!o.character.referenceFrames || typeof o.character.referenceFrames !== 'object') {
      o.character.referenceFrames = {};
    }
    o.character.referenceFrames.frontPortrait = url;
    return JSON.stringify(o, null, 2);
  } catch {
    return jsonStr;
  }
}

function removePortraitFromCreativeJson(jsonStr) {
  try {
    const o = jsonStr.trim() ? JSON.parse(jsonStr) : {};
    if (o.character && typeof o.character === 'object' && o.character.referenceFrames && typeof o.character.referenceFrames === 'object') {
      o.character.referenceFrames.frontPortrait = '';
    }
    return JSON.stringify(o, null, 2);
  } catch {
    return jsonStr;
  }
}

const tones = [
  { value: 'funny', label: 'Funny', icon: '~', color: '#F59E0B' },
  { value: 'adventure', label: 'Adventure', icon: '~', color: '#6366F1' },
  { value: 'bedtime', label: 'Bedtime', icon: '~', color: '#8B5CF6' },
  { value: 'educational', label: 'Educational', icon: '~', color: '#10B981' },
];
const visualStyles = [
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'realistic', label: 'Realistic' },
  { value: 'anime', label: 'Anime' },
  { value: 'watercolor', label: 'Watercolor' },
];
/** Create-agent visual style ids → closest option on this page. */
const AGENT_STYLE_TO_SETUP = {
  pixar_cinematic: 'cartoon',
  claymation: 'cartoon',
  comic_book: 'cartoon',
  minimalist_flat: 'cartoon',
  studio_ghibli: 'anime',
  photoreal_cinematic: 'realistic',
  hyperrealistic: 'realistic',
  cyberpunk_neon: 'realistic',
  noir_film: 'realistic',
  watercolor_storybook: 'watercolor',
};
const lengths = [
  { value: 'short', label: 'Short (3 scenes)' },
  { value: 'medium', label: 'Medium (5 scenes)' },
  { value: 'long', label: 'Long (8 scenes)' },
];

/** Target runtime for OpenAI multi-part plot + blueprint premise hint (minutes). */
const estimatedMovieTimes = [
  { value: 5, label: 'About 5 minutes' },
  { value: 10, label: 'About 10 minutes' },
  { value: 15, label: 'About 15 minutes' },
  { value: 20, label: 'About 20 minutes' },
  { value: 30, label: 'About 30 minutes' },
  { value: 45, label: 'About 45 minutes' },
  { value: 60, label: 'About 60 minutes' },
];

const imageProviders = [
  { value: 'nano_banana', label: 'Gemini Nano Banana' },
  { value: 'veo3', label: 'Gemini images (GEMINI_API_KEY)' },
  { value: 'minimax', label: 'MiniMax Hailuo' },
  { value: 'runway', label: 'Runway Gen4 text/image → image (RUNWAYML_API_SECRET)' },
  { value: 'inhouse', label: 'Inhouse' },
];

const sceneVideoEngines = [
  { value: 'nano_banana', label: 'Nano Banana slideshow (per scene)' },
  { value: 'minimax', label: 'MiniMax Hailuo AI video (per scene)' },
  { value: 'veo3', label: 'Google Veo 3.x video (Gemini API, GEMINI_API_KEY)' },
  { value: 'runway', label: 'Runway ML video (RUNWAYML_API_SECRET)' },
  { value: 'seedance', label: 'Seedance 2 video (Runway API, RUNWAYML_API_SECRET)' },
  { value: 'seedance_2_5', label: 'Seedance 2.5 video (Runway API, RUNWAYML_API_SECRET)' },
  { value: 'chatgpt_astra', label: 'ChatGPT Astra video (OpenAI Videos / OPENAI_API_KEY)' },
  { value: 'fable_5_1', label: 'Fable 5.1 video (renders via Seedance 2.5)' },
  { value: 'inhouse', label: 'Inhouse' },
];

const runwayImageToVideoModels = [
  { value: '__default__', label: 'Default (env/story)' },
  { value: 'gen4_turbo', label: 'gen4_turbo' },
  { value: 'gen4.5', label: 'gen4.5' },
  { value: 'seedance2', label: 'seedance2' },
  { value: 'seedance2_5', label: 'seedance2_5' },
  { value: 'veo3.1', label: 'veo3.1' },
  { value: 'veo3.1_fast', label: 'veo3.1_fast' },
  { value: 'veo3', label: 'veo3' },
  { value: 'gen3a_turbo', label: 'gen3a_turbo' },
];

const runwayTextToVideoModels = [
  { value: '__default__', label: 'Default (env/story)' },
  { value: 'gen4.5', label: 'gen4.5' },
  { value: 'seedance2', label: 'seedance2' },
  { value: 'seedance2_5', label: 'seedance2_5' },
  { value: 'veo3.1', label: 'veo3.1' },
  { value: 'veo3.1_fast', label: 'veo3.1_fast' },
  { value: 'veo3', label: 'veo3' },
];

import { BG_MUSIC_PROVIDER_OPTIONS } from '@/lib/bgMusicProviders';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const STORY_SETUP_DRAFT_KEY = 'twin_tales_story_setup_draft';
/** Browser draft for story setup form (local + optional server id). */
const STORY_LOCAL_DRAFT_KEY = 'twin_tales_story_setup_local_draft_v1';

const aspectRatios = [
  { value: '16:9', label: '16:9 Widescreen' },
  { value: '4:3', label: '4:3 Classic' },
  { value: '1:1', label: '1:1 Square' },
  { value: '3:4', label: '3:4 Portrait' },
  { value: '9:16', label: '9:16 Vertical' },
];

/** Map API story document → setup form slice (dialogue & other types). */
const storyDocumentToFormSlice = (story) => ({
  title: story.title ?? '',
  story_type: story.story_type ?? 'dialogue_story',
  tone: story.tone ?? 'funny',
  visual_style: story.visual_style ?? 'cartoon',
  video_style: story.video_style ?? 'narrated',
  moral_theme: story.moral_theme ?? '',
  story_length: story.story_length ?? 'medium',
  estimated_movie_minutes:
    typeof story.estimated_movie_minutes === 'number' && story.estimated_movie_minutes >= 3
      ? story.estimated_movie_minutes
      : 15,
  image_provider: story.image_provider ?? 'nano_banana',
  default_scene_video_provider: story.default_scene_video_provider ?? 'nano_banana',
  runway_model_image: story?.runway_video_options?.model_image ?? '',
  runway_model_text: story?.runway_video_options?.model_text ?? '',
  image_aspect_ratio: story.image_aspect_ratio ?? '16:9',
  character_ids: Array.isArray(story.character_ids) ? [...story.character_ids] : [],
  narrator_character_id: story.narrator_character_id ?? '',
  user_topic: story.user_topic ?? '',
  user_full_story: story.user_full_story ?? '',
  genre: story.genre ?? '',
  bg_music_provider: story.bg_music_provider != null && story.bg_music_provider !== '' ? story.bg_music_provider : 'none',
});

const dialogueGenres = [
  { value: '__general__', label: 'General' },
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'comedy', label: 'Comedy' },
  { value: 'mystery', label: 'Mystery' },
  { value: 'sci_fi', label: 'Science fiction' },
  { value: 'slice_of_life', label: 'Slice of life' },
  { value: 'bedtime', label: 'Gentle bedtime' },
];

export const StorySetupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const suggestion = location.state?.suggestion;
  const storyFlow = location.state?.storyFlow
    ?? (location.pathname === '/dialogue/create'
      ? STORY_FLOWS.find((f) => f.id === 'dialogue')
      : undefined);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ollamaDrafting, setOllamaDrafting] = useState(false);
  const [openaiPlotLoading, setOpenaiPlotLoading] = useState(false);
  const [detectingCharacters, setDetectingCharacters] = useState(false);
  const [inputMode, setInputMode] = useState('topic');
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftStoryId, setDraftStoryId] = useState(null);
  const [pendingLocalDraft, setPendingLocalDraft] = useState(null);
  const [dialoguePanelOpen, setDialoguePanelOpen] = useState(
    storyFlow?.story_type === 'dialogue_story',
  );
  const [dialogueStoriesList, setDialogueStoriesList] = useState([]);
  const [loadingDialogueList, setLoadingDialogueList] = useState(false);
  const [loadingImportedStoryId, setLoadingImportedStoryId] = useState(null);
  const [cinematicMovie, setCinematicMovie] = useState(null);
  const [cinematicGenerationMeta, setCinematicGenerationMeta] = useState(null);
  const [cinematicLoading, setCinematicLoading] = useState(false);
  const [charProfileOpen, setCharProfileOpen] = useState(false);
  const [charProfileId, setCharProfileId] = useState(null);
  const [charProfileName, setCharProfileName] = useState('');
  const [charProfileJson, setCharProfileJson] = useState('');
  const [charProfileSnapshotJson, setCharProfileSnapshotJson] = useState(null);
  const [charProfileSaving, setCharProfileSaving] = useState(false);
  const [charPhotoUploading, setCharPhotoUploading] = useState(false);
  const [charPhotoRemoving, setCharPhotoRemoving] = useState(false);
  const [refSheetPreviewOpen, setRefSheetPreviewOpen] = useState(false);
  const [refSheetPreviewText, setRefSheetPreviewText] = useState('');
  const [refSheetBusy, setRefSheetBusy] = useState(false);
  const [runwayAvatarBusy, setRunwayAvatarBusy] = useState(false);
  const [runwayAvatarVoice, setRunwayAvatarVoice] = useState('mia');
  const [regeneratingAngleKey, setRegeneratingAngleKey] = useState('');
  const charPortraitFileRef = useRef(null);
  const preselectedCharsApplied = useRef(false);
  const [form, setForm] = useState({
    title: suggestion?.theme ? `${suggestion.theme} Adventure` : '',
    story_type: storyFlow?.story_type || 'original',
    tone: storyFlow?.tone || suggestion?.tone || 'funny',
    visual_style: 'cartoon',
    video_style: storyFlow?.video_style || 'narrated',
    moral_theme: storyFlow?.moral_theme || suggestion?.moral || '',
    story_length: 'medium',
    estimated_movie_minutes: 15,
    image_provider: 'nano_banana',
    default_scene_video_provider: 'nano_banana',
    runway_model_image: '',
    runway_model_text: '',
    image_aspect_ratio: '16:9',
    character_ids: [],
    narrator_character_id: '',
    user_topic: suggestion?.theme ? `${suggestion.theme} - ${suggestion.reason || ''}`.trim() : '',
    user_full_story: '',
    genre: '',
    bg_music_provider: 'none',
    image_style_notes: '',
  });

  const agentHandoff = useMemo(() => readAgentHandoff(location.state), [location.state]);
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    const nav = getIntakeNavPrefill(location.state);
    if (!nav?.text && !nav?.title) return;
    const { prefill, recommended } = nav;
    const fromAgent = Boolean(readAgentHandoff(location.state));
    const agentScript = fromAgent ? String(prefill.fullScript || '').trim() : '';
    const useFullStory = Boolean(agentScript) && nav.mode === 'script';
    const style = nav.visualStyle
      ? pickOption(nav.visualStyle, visualStyles, AGENT_STYLE_TO_SETUP[nav.visualStyle])
      : undefined;
    setForm((prev) => ({
      ...prev,
      title: nav.title || prev.title,
      user_topic: nav.text || prev.user_topic,
      user_full_story: useFullStory ? agentScript : prev.user_full_story,
      tone: nav.tone || prev.tone,
      visual_style: style || prev.visual_style,
      image_style_notes: prefill.imageStyleNotes || prev.image_style_notes || '',
      story_type: location.pathname.includes('dialogue') ? 'dialogue_story' : prev.story_type,
      ...(fromAgent
        ? {
            genre: pickOption(prefill.genreId || recommended.genre_id, dialogueGenres, prev.genre),
            image_aspect_ratio: pickOption(prefill.aspectRatio, aspectRatios, prev.image_aspect_ratio),
            image_provider: pickOption(prefill.imageProvider, imageProviders, prev.image_provider),
            estimated_movie_minutes: nearestValue(
              durationMinutes(prefill.durationSeconds, 0),
              estimatedMovieTimes.map((t) => t.value),
              prev.estimated_movie_minutes,
            ),
          }
        : {}),
    }));
    if (useFullStory) setInputMode('full');
    if (fromAgent) setHandoffReady(true);
  }, [location.state, location.pathname]);

  const loadCharacters = useCallback(async () => {
    try {
      const { data } = await api.get('/characters');
      setCharacters(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load characters');
    }
  }, []);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORY_LOCAL_DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.v !== 1 || !d.form || typeof d.form !== 'object') return;
      setPendingLocalDraft(d);
    } catch {
      /* ignore */
    }
  }, []);

  const loadDialogueStoriesForPanel = useCallback(async () => {
    setLoadingDialogueList(true);
    try {
      const { data } = await api.get('/stories');
      const rows = Array.isArray(data) ? data : [];
      const filtered = rows
        .filter((s) => (s.story_type || '').toLowerCase() === 'dialogue_story')
        .sort((a, b) => {
          const ta = new Date(a.created_at || 0).getTime();
          const tb = new Date(b.created_at || 0).getTime();
          return tb - ta;
        });
      setDialogueStoriesList(filtered);
    } catch {
      toast.error('Could not load dialogue stories');
    } finally {
      setLoadingDialogueList(false);
    }
  }, []);

  useEffect(() => {
    if (dialoguePanelOpen) loadDialogueStoriesForPanel();
  }, [dialoguePanelOpen, loadDialogueStoriesForPanel]);

  const loadStoryIntoForm = async (summary) => {
    if (!summary?.id) return;
    setLoadingImportedStoryId(summary.id);
    try {
      const { data: full } = await api.get(`/stories/${summary.id}`);
      const slice = storyDocumentToFormSlice(full);
      setForm((prev) => ({ ...prev, ...slice }));
      const cm = full.cinematic_movie ?? null;
      setCinematicMovie(cm && typeof cm === 'object' ? cm : null);
      setCinematicGenerationMeta(full.cinematic_generation_meta && typeof full.cinematic_generation_meta === 'object'
        ? full.cinematic_generation_meta
        : null);
      const ft = (full.user_full_story || '').trim();
      setInputMode(ft ? 'full' : 'topic');
      const st = (full.status || 'draft').toLowerCase();
      setDraftStoryId(st === 'draft' ? full.id : null);
      try {
        localStorage.setItem(
          STORY_LOCAL_DRAFT_KEY,
          JSON.stringify({
            v: 1,
            form: { ...slice, story_type: slice.story_type || 'dialogue_story' },
            inputMode: ft ? 'full' : 'topic',
            cinematicMovie: cm && typeof cm === 'object' ? cm : null,
            cinematicGenerationMeta:
              full.cinematic_generation_meta && typeof full.cinematic_generation_meta === 'object'
                ? full.cinematic_generation_meta
                : null,
            serverStoryId: st === 'draft' ? full.id : null,
            savedAt: new Date().toISOString(),
          }),
        );
      } catch {
        /* ignore */
      }
      setPendingLocalDraft(null);
      if (Array.isArray(full.characters) && full.characters.length) {
        setCharacters((prev) => {
          const byId = new Map(prev.map((c) => [c.id, c]));
          full.characters.forEach((c) => {
            if (c?.id) byId.set(c.id, c);
          });
          return Array.from(byId.values());
        });
      }
      toast.success(
        st === 'draft'
          ? 'Loaded draft into the form. Save draft or generate when ready.'
          : 'Loaded story into the form as a starting point — generating will create a new run unless you change the title and save a new draft first.',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load that story');
    } finally {
      setLoadingImportedStoryId(null);
    }
  };

  useEffect(() => {
    if (!location.state?.restoredFromCharacterBuilder) return;
    try {
      const raw = sessionStorage.getItem(STORY_SETUP_DRAFT_KEY);
      const pre = location.state?.selectedCharacters;
      if (raw) {
        const parsed = JSON.parse(raw);
        setForm((f) => {
          let next = { ...f };
          if (parsed.form && typeof parsed.form === 'object') {
            next = { ...next, ...parsed.form };
          }
          if (Array.isArray(pre) && pre.length) {
            next.character_ids = [...new Set([...(next.character_ids || []), ...pre])];
          }
          return next;
        });
        if (typeof parsed.inputMode === 'string') setInputMode(parsed.inputMode);
        if (parsed.cinematicMovie != null && typeof parsed.cinematicMovie === 'object') {
          setCinematicMovie(parsed.cinematicMovie);
          setCinematicGenerationMeta(
            parsed.cinematicGenerationMeta && typeof parsed.cinematicGenerationMeta === 'object'
              ? parsed.cinematicGenerationMeta
              : null,
          );
        }
        sessionStorage.removeItem(STORY_SETUP_DRAFT_KEY);
      } else if (Array.isArray(pre) && pre.length) {
        setForm((f) => ({
          ...f,
          character_ids: [...new Set([...f.character_ids, ...pre])],
        }));
      }
    } catch {
      sessionStorage.removeItem(STORY_SETUP_DRAFT_KEY);
    }
    preselectedCharsApplied.current = true;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.restoredFromCharacterBuilder, location.pathname, navigate]);

  useEffect(() => {
    if (preselectedCharsApplied.current) return;
    if (location.state?.restoredFromCharacterBuilder) return;
    const pre = location.state?.selectedCharacters;
    if (Array.isArray(pre) && pre.length) {
      preselectedCharsApplied.current = true;
      setForm((f) => ({
        ...f,
        character_ids: [...new Set([...f.character_ids, ...pre])],
      }));
    }
  }, [location.state]);

  const persistStoryDraftAndGoToCharacters = (navState) => {
    try {
      sessionStorage.setItem(
        STORY_SETUP_DRAFT_KEY,
        JSON.stringify({
          form,
          inputMode,
          cinematicMovie,
          cinematicGenerationMeta,
        }),
      );
    } catch {
      toast.error('Could not stash your story draft. Save your title elsewhere, then try again.');
      return;
    }
    navigate('/characters', {
      state: {
        returnTo: '/stories/new',
        ...navState,
      },
    });
  };

  const goAddCharacterInBuilder = () => persistStoryDraftAndGoToCharacters({ openNewCharacterForm: true });

  const goEditCharacterInBuilder = (c) => persistStoryDraftAndGoToCharacters({ editCharacterId: c.id });

  const deleteCharacter = async (c) => {
    if (!window.confirm(`Delete “${c.name}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/characters/${c.id}`);
      toast.success('Character deleted');
      setForm((f) => ({
        ...f,
        character_ids: f.character_ids.filter((id) => id !== c.id),
      }));
      await loadCharacters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete character');
    }
  };

  const toggleChar = (id) => {
    setForm(prev => ({
      ...prev,
      character_ids: prev.character_ids.includes(id)
        ? prev.character_ids.filter(c => c !== id)
        : [...prev.character_ids, id]
    }));
  };

  /** Characters that belong to this story setup only (in cast order). */
  const storyCharacters = form.character_ids
    .map((id) => characters.find((c) => c.id === id))
    .filter(Boolean);
  const libraryNotInStory = characters.filter((c) => !form.character_ids.includes(c.id));
  const narratorCharacterRecord = characters.find((c) => c.id === form.narrator_character_id);

  /** Text sent to character detection (topic + full story if both filled). */
  const getScriptTextForCharacterDetection = () => {
    const parts = [form.user_topic?.trim(), form.user_full_story?.trim()].filter(Boolean);
    return parts.join('\n\n');
  };

  const narrativeCharDetectTimeoutMs =
    Number(process.env.REACT_APP_CHARACTER_DETECT_TIMEOUT_MS) > 0
      ? Number(process.env.REACT_APP_CHARACTER_DETECT_TIMEOUT_MS)
      : 20 * 60 * 1000;

  const openCreativeProfileDialog = async (c) => {
    if (!c?.id) return;
    setCharProfileId(c.id);
    setCharProfileName(String(c.name || 'Character'));
    try {
      const { data } = await api.get(`/characters/${c.id}`, { timeout: 60000 });
      const merged = data?.creative_profile ?? defaultCreativeProfileFromCharacter(data || c);
      setCharProfileJson(JSON.stringify(merged, null, 2));
      setCharProfileSnapshotJson(
        data?.creative_profile_ai_snapshot
          ? JSON.stringify(data.creative_profile_ai_snapshot, null, 2)
          : null
      );
      const pv =
        data?.runway_live_avatar?.voice?.presetId ||
        data?.runway_live_avatar?.voice?.preset_id ||
        '';
      setRunwayAvatarVoice(typeof pv === 'string' && pv.trim() ? pv.trim().toLowerCase() : 'mia');
    } catch {
      toast.error('Could not load character for editing.');
      const merged = defaultCreativeProfileFromCharacter(c);
      setCharProfileJson(JSON.stringify(merged, null, 2));
      setCharProfileSnapshotJson(null);
      setRunwayAvatarVoice('mia');
    }
    setCharProfileOpen(true);
  };

  const resetCreativeProfileToSnapshot = () => {
    if (charProfileSnapshotJson) {
      setCharProfileJson(charProfileSnapshotJson);
      toast.success('Restored AI character analysis.');
      return;
    }
    const c = characters.find((x) => x.id === charProfileId);
    if (c) {
      setCharProfileJson(JSON.stringify(defaultCreativeProfileFromCharacter(c), null, 2));
      toast.info('No saved AI snapshot — reset to a template from basic fields.', { duration: 4000 });
    }
  };

  const creativeProfileReferenceFrames = useMemo(
    () => parseCreativeProfileReferenceFrames(charProfileJson),
    [charProfileJson],
  );

  const creativeProfileRefGeneration = useMemo(
    () => parseCreativeProfileReferenceGeneration(charProfileJson),
    [charProfileJson],
  );

  /** Per-angle regen prerequisites (must match backend reference-sheet/regenerate-angle). */
  const referenceSheetRegenDeps = useMemo(() => {
    const f = creativeProfileReferenceFrames;
    const hasMedia = (k) =>
      typeof f[k] === 'string' && f[k].trim().includes('/api/media/');
    return {
      faceFrontSheet: hasMedia('faceFront'),
      bodyFrontSheet: hasMedia('bodyFront'),
    };
  }, [creativeProfileReferenceFrames]);

  const storySetupStoredSeedDisplay =
    creativeProfileRefGeneration?.seed ??
    characters.find((x) => x.id === charProfileId)?.reference_sheet_seed ??
    null;

  const creativeProfilePortraitPreviewSrc = useMemo(() => {
    const fromFrames = typeof creativeProfileReferenceFrames.frontPortrait === 'string'
      ? creativeProfileReferenceFrames.frontPortrait.trim()
      : '';
    const currentChar = characters.find((x) => x.id === charProfileId) || null;
    const fromCharacter = typeof currentChar?.reference_image === 'string'
      ? currentChar.reference_image.trim()
      : '';
    const raw = fromFrames || fromCharacter;
    if (!raw) return '';
    if (raw.startsWith('/api/')) return `${BACKEND_URL}${raw}`;
    return raw;
  }, [charProfileId, characters, creativeProfileReferenceFrames]);

  const profileCharRunwayLiveAvatar = useMemo(() => {
    const ch = characters.find((x) => x.id === charProfileId);
    return ch?.runway_live_avatar && typeof ch.runway_live_avatar === 'object' ? ch.runway_live_avatar : null;
  }, [characters, charProfileId]);

  const previewCharacterReferenceSheet = async () => {
    if (!charProfileId) return;
    try {
      const { data } = await api.post(`/characters/${charProfileId}/reference-sheet/preview`);
      const sheet = data?.characterReferenceSheet;
      setRefSheetPreviewText(sheet ? JSON.stringify(sheet, null, 2) : '{}');
      setRefSheetPreviewOpen(true);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not build reference sheet JSON'));
    }
  };

  const regenerateRunwayAngle = async (angleKey) => {
    if (!charProfileId) return;
    setRegeneratingAngleKey(angleKey);
    try {
      const { data } = await api.post(
        `/characters/${charProfileId}/reference-sheet/regenerate-angle`,
        { angle_key: angleKey },
        { timeout: 360000 },
      );
      toast.success(`${angleKey} regenerated (same consistency seed)`);
      if (data?.creative_profile && typeof data.creative_profile === 'object') {
        setCharProfileJson(JSON.stringify(data.creative_profile, null, 2));
      }
      await loadCharacters();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not regenerate this angle'));
    } finally {
      setRegeneratingAngleKey('');
    }
  };

  const generateRunwayReferenceSheet = async () => {
    if (!charProfileId) return;
    setRefSheetBusy(true);
    try {
      const { data } = await api.post(
        `/characters/${charProfileId}/reference-sheet/generate-runway`,
        { parallel: false },
        { timeout: 920000 },
      );
      const nErr = Array.isArray(data?.errors) ? data.errors.length : 0;
      if (nErr > 0) {
        toast.warning(
          `${8 - nErr}/8 Runway angles saved. Failed: ${data.errors.map((e) => e.key).join(', ') || 'see network response'}`,
          { duration: 8000 },
        );
      } else {
        toast.success('Saved all 8 Runway turnaround angles to creative_profile.character.referenceFrames.');
      }
      if (data?.creative_profile && typeof data.creative_profile === 'object') {
        setCharProfileJson(JSON.stringify(data.creative_profile, null, 2));
      } else {
        const { data: refreshed } = await api.get(`/characters/${charProfileId}`, { timeout: 60000 });
        setCharProfileJson(
          JSON.stringify(
            refreshed?.creative_profile ?? defaultCreativeProfileFromCharacter(refreshed || {}),
            null,
            2,
          ),
        );
      }
      await loadCharacters();
    } catch (err) {
      toast.error(formatApiError(err, 'Runway reference sheet generation failed'));
    } finally {
      setRefSheetBusy(false);
    }
  };

  const createRunwayLiveAvatar = async () => {
    if (!charProfileId || !referenceSheetRegenDeps.faceFrontSheet) return;
    setRunwayAvatarBusy(true);
    try {
      const { data } = await api.post(
        `/characters/${charProfileId}/runway-live-avatar`,
        { voice_preset_id: runwayAvatarVoice },
        { timeout: 130000 },
      );
      const av = data?.runway_live_avatar;
      const st = av?.status != null ? String(av.status) : '';
      const aid = av?.id != null ? String(av.id) : '';
      toast.success(
        aid
          ? `Runway Live avatar created (id ${aid.slice(0, 8)}…).${st ? ` Status: ${st}.` : ''}`
          : `Runway Live avatar response received.${st ? ` Status: ${st}.` : ''}`,
      );
      if (data?.character) {
        setCharacters((prev) => prev.map((x) => (x.id === data.character.id ? { ...x, ...data.character } : x)));
      } else {
        await loadCharacters();
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Could not create Runway Live avatar'));
    } finally {
      setRunwayAvatarBusy(false);
    }
  };

  const saveCreativeProfile = async () => {
    if (!charProfileId) return;
    let parsed;
    try {
      parsed = JSON.parse(charProfileJson);
    } catch (e) {
      toast.error('Invalid JSON — fix syntax before saving.');
      return;
    }
    setCharProfileSaving(true);
    try {
      await api.put(`/characters/${charProfileId}`, { creative_profile: parsed });
      await loadCharacters();
      toast.success('Creative profile saved.');
      setCharProfileOpen(false);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save creative profile'));
    } finally {
      setCharProfileSaving(false);
    }
  };

  const onPortraitFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !charProfileId) return;
    const ok = /^image\/(jpeg|jpg|png|webp)$/i.test(file.type);
    if (!ok) {
      toast.error('Please choose a JPEG, PNG, or WEBP image.');
      return;
    }
    setCharPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: updated } = await api.post(`/characters/${charProfileId}/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const url = updated?.reference_image;
      await loadCharacters();
      if (url) {
        setCharProfileJson((prev) => mergePortraitUrlIntoCreativeJson(prev, url));
      }
      toast.success('Portrait uploaded — reference image stored; front portrait slot updated below.');
    } catch (err) {
      toast.error(formatApiError(err, 'Upload failed'));
    } finally {
      setCharPhotoUploading(false);
    }
  };

  const removePortrait = async () => {
    if (!charProfileId) return;
    if (!window.confirm('Remove this uploaded portrait from the character?')) return;
    setCharPhotoRemoving(true);
    try {
      await api.delete(`/characters/${charProfileId}/photo`);
      await loadCharacters();
      setCharProfileJson((prev) => removePortraitFromCreativeJson(prev));
      toast.success('Portrait removed from character profile.');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not remove portrait'));
    } finally {
      setCharPhotoRemoving(false);
    }
  };

  const detectAndCreateCharacters = async () => {
    const full_script = getScriptTextForCharacterDetection();
    if (!full_script.trim()) {
      toast.error('Enter a topic or full story first so we can scan for names.');
      return;
    }
    setDetectingCharacters(true);
    try {
      const { data } = await api.post(
        '/narration/characters/auto-create',
        {
          title: form.title?.trim() || 'Story',
          full_script,
          story_type: form.story_type || '',
        },
        { timeout: narrativeCharDetectTimeoutMs },
      );
      const nextCharacters = [...(data.existing || []), ...(data.created || [])];
      if (!nextCharacters.length) {
        toast.info('No named characters were detected in your text.');
        return;
      }
      setCharacters((prev) => {
        const byId = new Map(prev.map((c) => [c.id, c]));
        nextCharacters.forEach((c) => byId.set(c.id, c));
        return Array.from(byId.values());
      });
      setForm((prev) => ({
        ...prev,
        character_ids: Array.from(new Set([...prev.character_ids, ...(data.character_ids || [])])),
      }));
      const createdCount = data.created?.length || 0;
      const existingCount = data.existing?.length || 0;
      toast.success(
        `Detected ${nextCharacters.length} character${nextCharacters.length === 1 ? '' : 's'} (${createdCount} new, ${existingCount} existing)`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not detect characters from your text');
    } finally {
      setDetectingCharacters(false);
    }
  };

  const draftDialogueScriptFromOllama = async () => {
    if (form.story_type !== 'dialogue_story') return;
    const plot = form.user_topic.trim();
    if (plot.length < 8) {
      toast.error('Enter a short plot or topic first (at least a sentence).');
      return;
    }
    setOllamaDrafting(true);
    try {
      const { data } = await api.post('/stories/dialogue-script-from-plot', {
        plot,
        story_type: form.story_type,
        title: form.title,
        tone: form.tone,
        genre: form.genre || '',
        moral_theme: form.moral_theme,
        story_length: form.story_length,
        visual_style: form.visual_style,
        character_ids: form.character_ids,
        narrator_character_id: form.narrator_character_id || undefined,
      });
      const script = data?.script || '';
      if (!script.trim()) {
        toast.error('Ollama returned an empty script.');
        return;
      }
      setForm((f) => ({ ...f, user_full_story: script }));
      setInputMode('full');
      toast.success(
        `Draft script ready (${data?.model || 'configured model'} — ChatGPT when the API key is set, otherwise Ollama). Review the text, then generate your story.`,
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Could not draft script with Ollama. Is it running with the model pulled?',
      );
    } finally {
      setOllamaDrafting(false);
    }
  };

  const generateOpenAIPlotFromTopic = async () => {
    const plot = form.user_topic.trim();
    if (plot.length < 8) {
      toast.error('Enter a short plot or topic first (at least a sentence).');
      return;
    }
    if (!form.title.trim() || form.title.trim().length < 2) {
      toast.error('Add a story title first — the model infers genre mainly from the title.');
      return;
    }
    setOpenaiPlotLoading(true);
    try {
      const { data } = await api.post(
        '/stories/openai-plot-from-topic',
        {
          title: form.title,
          user_topic: form.user_topic,
          estimated_movie_minutes: form.estimated_movie_minutes ?? 15,
          tone: form.tone,
          visual_style: form.visual_style,
          story_type: form.story_type || 'original',
        },
        { timeout: 180000 },
      );
      const full = (data?.full_story || '').trim();
      if (!full) {
        toast.error('OpenAI returned an empty plot. Try again or shorten the topic.');
        return;
      }
      const g = (data?.detected_genre || '').trim();
      const theme = (data?.central_theme || '').trim();
      const label = (data?.genre_label || '').trim();
      setForm((f) => {
        const next = {
          ...f,
          moral_theme: theme || f.moral_theme,
          user_full_story: full,
        };
        if (g && g !== 'general') next.genre = g;
        return next;
      });
      setInputMode('full');
      toast.success(
        `Plot and theme ready via OpenAI (${data?.model || 'OpenAI'}).${
          label ? ` Genre: ${label}.` : g && g !== 'general' ? ` Genre: ${g.replace(/_/g, ' ')}.` : ''
        } Review or edit the full story text, then generate.`,
      );
    } catch (err) {
      toast.error(formatApiError(err, 'OpenAI plot generation failed. Check API key and quotas.'));
    } finally {
      setOpenaiPlotLoading(false);
    }
  };

const buildStoryPayload = () => {
  const {
    runway_model_image,
    runway_model_text,
    ...rest
  } = form;
  return {
    ...rest,
    story_type: form.story_type || 'original',
    narrator_character_id: form.narrator_character_id || undefined,
    runway_video_options:
      (runway_model_image || runway_model_text)
        ? {
            model_image: runway_model_image || null,
            model_text: runway_model_text || null,
          }
        : undefined,
  };
};

  /** Same fields the server persists for drafts (story form + cinematic blueprint). */
  const buildDraftSyncPayload = () => {
    const base = buildStoryPayload();
    const hasMovie = cinematicMovie != null && typeof cinematicMovie === 'object';
    return {
      ...base,
      ...(hasMovie
        ? {
            cinematic_movie: cinematicMovie,
            cinematic_generation_meta:
              cinematicGenerationMeta && typeof cinematicGenerationMeta === 'object'
                ? cinematicGenerationMeta
                : null,
          }
        : {}),
    };
  };

  const writeLocalDraftFile = (payload, serverId) => {
    try {
      localStorage.setItem(
        STORY_LOCAL_DRAFT_KEY,
        JSON.stringify({
          v: 1,
          form: payload,
          inputMode,
          cinematicMovie: cinematicMovie ?? null,
          cinematicGenerationMeta: cinematicGenerationMeta ?? null,
          serverStoryId: serverId || null,
          savedAt: new Date().toISOString(),
        }),
      );
    } catch {
      toast.error('Could not save draft in your browser storage');
    }
  };

  const saveDraft = async () => {
    const payload = buildDraftSyncPayload();
    const formSliceForBrowser = buildStoryPayload();
    writeLocalDraftFile(formSliceForBrowser, draftStoryId);
    setPendingLocalDraft(null);
    if (!payload.title?.trim()) {
      toast.success('Draft saved on this device. Add a title to also save a copy in your library.');
      return;
    }
    setSavingDraft(true);
    try {
      if (draftStoryId) {
        await api.patch(`/stories/${draftStoryId}`, payload);
        writeLocalDraftFile(formSliceForBrowser, draftStoryId);
        toast.success('Draft saved');
      } else {
        const { data: story } = await api.post('/stories', payload);
        setDraftStoryId(story.id);
        writeLocalDraftFile(formSliceForBrowser, story.id);
        toast.success('Draft saved to your library');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save draft to the server');
    } finally {
      setSavingDraft(false);
    }
  };

  const restoreLocalDraft = () => {
    if (!pendingLocalDraft?.form) return;
    setForm((prev) => ({ ...prev, ...pendingLocalDraft.form }));
    if (typeof pendingLocalDraft.inputMode === 'string') setInputMode(pendingLocalDraft.inputMode);
    if (pendingLocalDraft.serverStoryId) setDraftStoryId(pendingLocalDraft.serverStoryId);
    const cm =
      pendingLocalDraft.cinematicMovie != null && typeof pendingLocalDraft.cinematicMovie === 'object'
        ? pendingLocalDraft.cinematicMovie
        : null;
    setCinematicMovie(cm);
    setCinematicGenerationMeta(
      pendingLocalDraft.cinematicGenerationMeta != null &&
        typeof pendingLocalDraft.cinematicGenerationMeta === 'object'
        ? pendingLocalDraft.cinematicGenerationMeta
        : null,
    );
    setPendingLocalDraft(null);
    toast.success('Draft restored');
  };

  const discardLocalDraft = () => {
    try {
      localStorage.removeItem(STORY_LOCAL_DRAFT_KEY);
    } catch {
      /* ignore */
    }
    setPendingLocalDraft(null);
    setDraftStoryId(null);
    setCinematicMovie(null);
    setCinematicGenerationMeta(null);
    toast.info('Stored draft cleared');
  };

  const generateCinematicBlueprint = async () => {
    const topic = (form.user_topic || '').trim();
    const full = (form.user_full_story || '').trim();
    if (topic.length < 8 && full.length < 20) {
      toast.error('Add a longer topic or paste story text first (needed as source material for the blueprint).');
      return;
    }
    setCinematicLoading(true);
    try {
      const cinematicTimeoutMs =
        Number(process.env.REACT_APP_CINEMATIC_TIMEOUT_MS) > 0
          ? Number(process.env.REACT_APP_CINEMATIC_TIMEOUT_MS)
          : 45 * 60 * 1000;
      const { data } = await api.post(
        '/stories/cinematic-blueprint',
        {
          title: form.title,
          user_topic: form.user_topic,
          user_full_story: form.user_full_story,
          tone: form.tone,
          visual_style: form.visual_style,
          moral_theme: form.moral_theme,
          genre: form.genre,
          story_length: form.story_length,
          estimated_movie_minutes: form.estimated_movie_minutes ?? undefined,
        },
        { timeout: cinematicTimeoutMs },
      );
      const mv = data.movie && typeof data.movie === 'object' ? data.movie : null;
      setCinematicMovie(mv);
      setCinematicGenerationMeta(data.generation_meta && typeof data.generation_meta === 'object' ? data.generation_meta : null);
      const steps = data.generation_meta?.steps || [];
      const modeHint =
        steps.some((s) =>
          ['two_phase_fallback', 'two_phase_only', 'multistep_outline_and_scenes'].includes(s.phase)
        )
          ? ' Used multi-step generation (outline + scenes per segment).'
          : '';
      const enrichHint =
        data.generation_meta?.enrichment === 'applied'
          ? ' Empty fields were filled to match tone/premise.'
          : '';
      const granularHint =
        data.generation_meta?.granular_field_phase === 'applied'
          ? ' Per-section field fill (Qwen) completed.'
          : '';
      toast.success(
        `Cinematic blueprint ready (${data.model || 'configured LLM'}).${modeHint}${enrichHint}${granularHint} Edit below or copy JSON.`,
      );
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          'Blueprint generation failed. Check CHOSEN_LLM (OpenAI / Gemini / Ollama), API keys, quotas, and backend logs.'
        )
      );
    } finally {
      setCinematicLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Give your story a title'); return; }
    if (inputMode === 'topic' && !form.user_topic.trim()) { toast.error('Enter a story topic'); return; }
    if (inputMode === 'full' && !form.user_full_story.trim()) { toast.error('Enter your story text'); return; }
    setLoading(true);
    try {
      const storyPayload = buildDraftSyncPayload();
      let storyId = draftStoryId;
      if (storyId) {
        await api.patch(`/stories/${storyId}`, storyPayload);
      } else {
        const { data: story } = await api.post('/stories', storyPayload);
        storyId = story.id;
      }
      const { data: job } = await api.post(`/stories/${storyId}/generate`);
      try {
        localStorage.removeItem(STORY_LOCAL_DRAFT_KEY);
      } catch {
        /* ignore */
      }
      setDraftStoryId(null);
      toast.success('Story generation started!');
      navigate(`/stories/${storyId}/generate?job=${job.job_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create story');
    } finally { setLoading(false); }
  };

  return (
    <div
      className={`relative min-h-screen pb-20 transition-[padding] duration-200 ease-out ${
        dialoguePanelOpen ? 'lg:pr-[min(20rem,100vw)]' : ''
      }`}
      data-testid="story-setup-page"
    >
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel={storyFlow?.title || 'This story'}
        busy={loading}
        onStart={handleCreate}
        fields={[
          {
            id: 'title',
            label: 'Story title',
            valid: Boolean(form.title.trim()),
            render: () => (
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. The Lost Star of Mira"
                autoFocus
              />
            ),
          },
          inputMode === 'full'
            ? {
                id: 'story',
                label: 'Your story text',
                valid: Boolean(form.user_full_story.trim()),
                render: () => (
                  <Textarea
                    value={form.user_full_story}
                    onChange={(e) => setForm((f) => ({ ...f, user_full_story: e.target.value }))}
                    rows={6}
                    placeholder="Paste or write the full story…"
                  />
                ),
              }
            : {
                id: 'topic',
                label: 'What is the story about?',
                valid: Boolean(form.user_topic.trim()),
                render: () => (
                  <Textarea
                    value={form.user_topic}
                    onChange={(e) => setForm((f) => ({ ...f, user_topic: e.target.value }))}
                    rows={4}
                    placeholder="A short idea or plot for the story…"
                  />
                ),
              },
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              {storyFlow && (
                <button
                  type="button"
                  onClick={() => navigate('/stories/flow')}
                  className="mb-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#64748B] border border-slate-100 shadow-sm hover:text-[#6366F1] transition-colors"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  {storyFlow.title}
                </button>
              )}
              <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
                Create a <span className="gradient-text">New Story</span>
              </h1>
              <p className="text-[#64748B] mt-1">
                {storyFlow
                  ? `Set up your ${storyFlow.title.toLowerCase()} and let AI shape the scenes.`
                  : 'Set up your story details and let AI do the magic'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={saveDraft}
              disabled={savingDraft}
              className="rounded-xl border-slate-200 shrink-0 gap-2"
              data-testid="story-setup-save-draft-btn"
            >
              {savingDraft ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save draft
            </Button>
          </div>
          {pendingLocalDraft && (
            <div className="mt-4 rounded-2xl border border-indigo-100 bg-[#F8FAFF] px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-[#475569]">
                You have a{' '}
                {pendingLocalDraft.savedAt
                  ? `draft saved ${new Date(pendingLocalDraft.savedAt).toLocaleString()}`
                  : 'saved draft'}
                {pendingLocalDraft.serverStoryId ? ' (also in your library)' : ' (this device only)'}.
              </p>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button type="button" size="sm" className="rounded-lg" onClick={restoreLocalDraft}>
                  Restore draft
                </Button>
                <Button type="button" size="sm" variant="ghost" className="rounded-lg text-[#64748B]" onClick={discardLocalDraft}>
                  Discard
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-8">
          {/* Title */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <Label className="text-sm font-bold text-[#1E293B] mb-3 block">Story Title</Label>
            <Input
              data-testid="story-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="The Adventures of Little Bear..."
              className="h-14 rounded-xl border-2 border-slate-200 focus:border-[#6366F1] text-lg"
            />
          </div>

          {/* Story Input — near top so plot/script drives the rest of setup */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <Label className="text-sm font-bold text-[#1E293B] block mb-0">
                <TwinneeLogo className="w-4 h-4 inline mr-2 text-[#F59E0B]" />
                Story Input
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={detectAndCreateCharacters}
                disabled={detectingCharacters || !getScriptTextForCharacterDetection().trim()}
                className="rounded-xl gap-2 border-[#6366F1]/25 text-[#4F46E5] shrink-0"
                data-testid="story-setup-detect-characters-btn"
              >
                {detectingCharacters ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <TwinneeLogo className="w-3.5 h-3.5" />
                )}
                Detect &amp; Create Characters
              </Button>
            </div>
            <p className="text-xs text-[#64748B] mb-4 leading-relaxed">
              {form.story_type === 'dialogue_story' ? (
                <>
                  Local Ollama (Qwen-scale model) scans your text for names, infers traits and voice style, and adds library entries—then select them below for reference images. Other story types use cloud character detection when configured.
                </>
              ) : (
                <>
                  AI can detect named characters from your topic or full story, infer traits, assign a voice style, and add them to your library—then select them below for reference images.
                </>
              )}
            </p>
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setInputMode('topic')}
                data-testid="input-mode-topic"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${inputMode === 'topic' ? 'bg-[#6366F1] text-white' : 'bg-slate-100 text-[#64748B]'}`}
              >
                From Topic
              </button>
              <button
                type="button"
                onClick={() => setInputMode('full')}
                data-testid="input-mode-full"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${inputMode === 'full' ? 'bg-[#6366F1] text-white' : 'bg-slate-100 text-[#64748B]'}`}
              >
                Full Story
              </button>
            </div>

            {form.story_type === 'dialogue_story' && inputMode === 'topic' && (
              <div className="rounded-xl border border-indigo-100 bg-[#F8FAFF] px-4 py-3 mb-3 text-xs text-[#475569] leading-relaxed">
                <span className="font-semibold text-[#4338CA]">Dialogue story:</span>{' '}
                Character detection from text and scene JSON still use local Ollama when enabled. To grow a{' '}
                <span className="font-semibold">full plot from a topic</span>, use{' '}
                <span className="font-semibold">OpenAI</span> below (genre from title, theme + multi-part outline). Optional: draft a
                caps-lock dialogue script locally with Ollama after you have a plot.
              </div>
            )}

            {inputMode === 'topic' ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold text-[#475569] mb-1.5 block">Estimated movie runtime</Label>
                  <p className="text-xs text-[#94A3B8] mb-2">
                    Used when generating a structured plot with OpenAI (depth scales with length) and as a hint for the cinematic blueprint.
                  </p>
                  <Select
                    value={String(form.estimated_movie_minutes ?? 15)}
                    onValueChange={(v) =>
                      setForm({ ...form, estimated_movie_minutes: Number(v) || 15 })
                    }
                  >
                    <SelectTrigger
                      className="h-12 rounded-xl border-2 border-slate-200"
                      data-testid="story-estimated-runtime-select"
                    >
                      <SelectValue placeholder="Runtime" />
                    </SelectTrigger>
                    <SelectContent>
                      {estimatedMovieTimes.map((t) => (
                        <SelectItem key={t.value} value={String(t.value)}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  data-testid="story-topic-input"
                  value={form.user_topic}
                  onChange={(e) => setForm({ ...form, user_topic: e.target.value })}
                  placeholder="Two friends disagree about how to build a kite, until a storm teaches them teamwork..."
                  className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#6366F1]"
                />
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <Button
                    type="button"
                    disabled={
                      openaiPlotLoading || ollamaDrafting || form.user_topic.trim().length < 8 || !form.title.trim()
                    }
                    onClick={generateOpenAIPlotFromTopic}
                    data-testid="openai-plot-from-topic-btn"
                    className="w-full sm:w-auto rounded-xl bg-[#6366F1] text-white hover:bg-[#5558E3]"
                  >
                    {openaiPlotLoading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                    ) : (
                      <TwinneeLogo className="w-4 h-4 mr-2" />
                    )}
                    {openaiPlotLoading ? 'Generating plot…' : 'Generate plot & theme (OpenAI)'}
                  </Button>
                  {form.story_type === 'dialogue_story' && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={ollamaDrafting || openaiPlotLoading || form.user_topic.trim().length < 8}
                      onClick={draftDialogueScriptFromOllama}
                      data-testid="ollama-draft-dialogue-script-btn"
                      className="w-full sm:w-auto rounded-xl border-[#818CF8] text-[#4338CA] hover:bg-[#EEF2FF]"
                    >
                      {ollamaDrafting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 border-[#818CF8]/40 border-t-[#4338CA] rounded-full"
                        />
                      ) : (
                        <TwinneeLogo className="w-4 h-4 mr-2" />
                      )}
                      {ollamaDrafting ? 'Drafting…' : 'Optional: dialogue script (Ollama)'}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Textarea
                data-testid="story-full-text-input"
                value={form.user_full_story}
                onChange={(e) => setForm({ ...form, user_full_story: e.target.value })}
                placeholder="Once upon a time, there was a little dragon who lived in a big cave..."
                className="min-h-[150px] rounded-xl border-2 border-slate-200 focus:border-[#6366F1]"
              />
            )}
          </div>

          {/* Tone */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <Label className="text-sm font-bold text-[#1E293B] mb-4 block">Story Tone</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {tones.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm({ ...form, tone: t.value })}
                  data-testid={`tone-${t.value}`}
                  className={`p-4 rounded-2xl text-center font-medium text-sm transition-all border-2 ${
                    form.tone === t.value
                      ? 'border-[#6366F1] bg-[#EEF2FF] text-[#6366F1] shadow-md shadow-[#6366F1]/10'
                      : 'border-slate-100 hover:border-slate-200 text-[#64748B]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Visual Style & Length */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
              <Label className="text-sm font-bold text-[#1E293B] mb-3 block"><Palette className="w-4 h-4 inline mr-2 text-[#6366F1]" />Visual Style</Label>
              <Select value={form.visual_style} onValueChange={(v) => setForm({ ...form, visual_style: v })}>
                <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="visual-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {visualStyles.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
              <Label className="text-sm font-bold text-[#1E293B] mb-3 block"><BookOpen className="w-4 h-4 inline mr-2 text-[#F59E0B]" />Story Length</Label>
              <Select value={form.story_length} onValueChange={(v) => setForm({ ...form, story_length: v })}>
                <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-length-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {lengths.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Image Settings */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
              <Label className="text-sm font-bold text-[#1E293B] mb-3 block"><Film className="w-4 h-4 inline mr-2 text-[#6366F1]" />Image Model</Label>
              <Select value={form.image_provider} onValueChange={(v) => setForm({ ...form, image_provider: v })}>
                <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-image-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {imageProviders.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
              <Label className="text-sm font-bold text-[#1E293B] mb-3 block"><Film className="w-4 h-4 inline mr-2 text-[#F59E0B]" />Image Aspect Ratio</Label>
              <Select value={form.image_aspect_ratio} onValueChange={(v) => setForm({ ...form, image_aspect_ratio: v })}>
                <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-aspect-ratio-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {aspectRatios.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Default scene video engine (storyboard grid) */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <Label className="text-sm font-bold text-[#1E293B] mb-3 block"><Film className="w-4 h-4 inline mr-2 text-[#F97316]" />Default scene video (storyboard)</Label>
            <p className="text-xs text-[#64748B] mb-3">Used when you click &quot;Gen Video&quot; on each scene unless you pick another engine on the storyboard.</p>
            <Select value={form.default_scene_video_provider} onValueChange={(v) => setForm({ ...form, default_scene_video_provider: v })}>
              <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-default-scene-video-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sceneVideoEngines.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(form.default_scene_video_provider === 'runway'
              || form.default_scene_video_provider === 'seedance'
              || form.default_scene_video_provider === 'seedance_2_5'
              || form.default_scene_video_provider === 'fable_5_1') && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-[#475569] mb-1 block">Runway model (image → video)</Label>
                  <Select
                    value={form.runway_model_image ? form.runway_model_image : '__default__'}
                    onValueChange={(v) => setForm({ ...form, runway_model_image: v === '__default__' ? '' : v })}
                  >
                    <SelectTrigger className="h-10 rounded-xl border-2 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {runwayImageToVideoModels.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569] mb-1 block">Runway model (text → video)</Label>
                  <Select
                    value={form.runway_model_text ? form.runway_model_text : '__default__'}
                    onValueChange={(v) => setForm({ ...form, runway_model_text: v === '__default__' ? '' : v })}
                  >
                    <SelectTrigger className="h-10 rounded-xl border-2 border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {runwayTextToVideoModels.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Per-scene background music */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <Label className="text-sm font-bold text-[#1E293B] mb-3 block">
              <Music className="w-4 h-4 inline mr-2 text-[#A855F7]" />Background music (per scene)
            </Label>
            <p className="text-xs text-[#64748B] mb-3">
              After the story is generated, we infer each scene&apos;s mood and (when suitable) create short instrumental underscores via your chosen provider.
              Additional providers can be wired later; today MiniMax uses the same API key as MiniMax/Hailuo video.
            </p>
            <Select
              value={form.bg_music_provider}
              onValueChange={(v) => setForm({ ...form, bg_music_provider: v })}
            >
              <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-bg-music-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BG_MUSIC_PROVIDER_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.value === 'none' ? 'Off — no per-scene background music' : p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Genre — dialogue stories (guides Ollama plot expansion + scene tone) */}
          {form.story_type === 'dialogue_story' && (
            <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
              <Label className="text-sm font-bold text-[#1E293B] mb-2 block">
                Genre <span className="text-xs font-normal text-[#94A3B8]">(dialogue flow — optional)</span>
              </Label>
              <p className="text-xs text-[#64748B] mb-3 leading-relaxed">
                OpenAI plot generation can set this from your title. You can also pick a genre manually; it guides dialogue
                script expansion (Ollama / ChatGPT) and tone before scenes are generated.
              </p>
              <Select
                value={form.genre ? form.genre : '__general__'}
                onValueChange={(v) => setForm({ ...form, genre: v === '__general__' ? '' : v })}
              >
                <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-genre-select">
                  <SelectValue placeholder="Genre" />
                </SelectTrigger>
                <SelectContent>
                  {dialogueGenres.map((g) => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Moral Theme */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <Label className="text-sm font-bold text-[#1E293B] mb-3 block"><GraduationCap className="w-4 h-4 inline mr-2 text-[#10B981]" />Learning Theme (optional)</Label>
            <Input
              data-testid="moral-theme-input"
              value={form.moral_theme}
              onChange={(e) => setForm({ ...form, moral_theme: e.target.value })}
              placeholder="kindness, friendship, courage, honesty..."
              className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#6366F1]"
            />
          </div>

          {/* Qwen / Ollama — full cinematic movie blueprint (JSON) */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100 border-[#6366F1]/20">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                <Label className="text-sm font-bold text-[#1E293B] mb-1 block">
                  <Film className="w-4 h-4 inline mr-2 text-[#6366F1]" />
                  AI movie blueprint (Qwen / Ollama)
                </Label>
                <p className="text-xs text-[#64748B] leading-relaxed max-w-xl">
                  Generate a production-style JSON blueprint: segments, scenes, video/image prompts, dialogue, audio, camera, and post—optimized for Runway, MiniMax, ElevenLabs-style pipelines. Uses your title, topic or full story, tone, and length. Larger Qwen models (
                  <span className="font-mono">OLLAMA_CINEMATIC_MODEL</span>) work best for long JSON.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={generateCinematicBlueprint}
                disabled={cinematicLoading}
                className="rounded-xl gap-2 border-[#6366F1]/30 text-[#4338CA] shrink-0"
                data-testid="story-setup-generate-cinematic-blueprint"
              >
                {cinematicLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
                Generate blueprint
              </Button>
            </div>
            {cinematicMovie ? (
              <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/50 p-4 max-h-[min(70vh,56rem)] overflow-y-auto">
                <CinematicBlueprintEditor movie={cinematicMovie} onChange={setCinematicMovie} />
              </div>
            ) : (
              <p className="text-xs text-[#94A3B8]">No blueprint yet. Fill story input above, then generate.</p>
            )}
          </div>

          {/* Characters */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <Label className="text-sm font-bold text-[#1E293B] block mb-0"><Users className="w-4 h-4 inline mr-2 text-[#EC4899]" />Characters in this story</Label>
              <div className="flex flex-wrap items-center gap-2">
                {characters.length > 0 && (
                  <span className="text-xs text-[#64748B]">
                    {storyCharacters.length} in this story
                    {libraryNotInStory.length > 0
                      ? ` · ${libraryNotInStory.length} more saved (not shown)`
                      : ''}
                  </span>
                )}
                <Button type="button" variant="outline" size="sm" className="rounded-lg h-9" onClick={goAddCharacterInBuilder} data-testid="story-setup-add-character">
                  <Plus className="w-4 h-4 mr-1" /> Add character
                </Button>
                <Button type="button" variant="ghost" size="sm" className="rounded-lg h-9 text-[#64748B]" onClick={() => persistStoryDraftAndGoToCharacters({})}>
                  Open builder
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#64748B] mb-4">
              Only people in this story appear here. Use Detect &amp; Create (above), add new ones, or include someone you already saved from your library.
            </p>
            {libraryNotInStory.length > 0 && (
              <div className="mb-4 max-w-md">
                <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Add from saved library</Label>
                <Select
                  key={libraryNotInStory.map((x) => x.id).join(',')}
                  onValueChange={(id) => {
                    if (id && !form.character_ids.includes(id)) {
                      setForm((prev) => ({ ...prev, character_ids: [...prev.character_ids, id] }));
                    }
                  }}
                >
                  <SelectTrigger className="h-10 rounded-xl border-2 border-slate-200" data-testid="story-setup-add-from-library">
                    <SelectValue placeholder="Choose a saved character…" />
                  </SelectTrigger>
                  <SelectContent>
                    {libraryNotInStory.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {`${c.name} (${(c.role || 'character').replace(/_/g, ' ')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {characters.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-center">
                <p className="text-sm text-[#64748B] mb-3">You have no saved characters yet. Create one for this story, or use Detect &amp; Create above to infer cast from your text.</p>
                <Button type="button" onClick={goAddCharacterInBuilder} className="rounded-lg" data-testid="story-setup-add-character-empty">
                  <Plus className="w-4 h-4 mr-2" /> Add your first character
                </Button>
              </div>
            ) : storyCharacters.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-center">
                <p className="text-sm text-[#64748B] mb-3">
                  No characters in this story yet. Run Detect &amp; Create Characters on your script, add a new character, or pick someone from saved library above.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 max-h-[min(60vh,32rem)] overflow-y-auto pr-1 -mr-1">
                {storyCharacters.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`select-char-${c.id}`}
                    className="flex flex-col gap-2 p-4 rounded-xl border-2 transition-all border-[#6366F1] bg-[#EEF2FF]"
                  >
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        className="flex flex-1 min-w-0 items-start gap-3 text-left rounded-lg -m-1 p-1 hover:bg-white/60"
                        onClick={() => toggleChar(c.id)}
                        data-testid={`select-char-toggle-${c.id}`}
                      >
                        <Checkbox checked className="pointer-events-none mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-[#1E293B] truncate">{c.name}</p>
                          <p className="text-xs text-[#94A3B8] capitalize">{c.role}</p>
                        </div>
                      </button>
                      <div className="flex shrink-0 gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-[#64748B] hover:text-[#8B5CF6]"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCreativeProfileDialog(c);
                          }}
                          title="Creative profile & portrait"
                          data-testid={`creative-profile-char-${c.id}`}
                        >
                          <ImagePlus className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-[#64748B] hover:text-[#6366F1]"
                          onClick={(e) => {
                            e.stopPropagation();
                            goEditCharacterInBuilder(c);
                          }}
                          title="Edit"
                          data-testid={`edit-char-${c.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-[#64748B] hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCharacter(c);
                          }}
                          title="Delete"
                          data-testid={`delete-char-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Narrator as a character — voice traits for narration lines */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100">
            <Label className="text-sm font-bold text-[#1E293B] mb-2 block">
              <Mic className="w-4 h-4 inline mr-2 text-[#8B5CF6]" />
              Narrator (voice)
            </Label>
            <p className="text-xs text-[#64748B] mb-4 leading-relaxed">
              Choose a saved character for the <span className="font-medium text-[#475569]">narrator</span>: their voice provider, IDs, and traits control how narration and bridge lines sound (separate from dialogue cast). Create one with role “narrator” in the builder or reuse any character.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-md">
                <Select
                  value={form.narrator_character_id ? form.narrator_character_id : 'none'}
                  onValueChange={(v) =>
                    setForm((prev) => ({ ...prev, narrator_character_id: v === 'none' ? '' : v }))
                  }
                >
                  <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="story-narrator-character-select">
                    <SelectValue placeholder="Default — story voice only" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Default — story voice only (no narrator character)</SelectItem>
                    {characters.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                        {(c.role || '').replace(/_/g, ' ') ? ` (${String(c.role).replace(/_/g, ' ')})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.narrator_character_id && narratorCharacterRecord && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl h-10"
                  onClick={() => goEditCharacterInBuilder(narratorCharacterRecord)}
                  data-testid="story-setup-edit-narrator"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit narrator voice
                </Button>
              )}
            </div>
            {characters.length === 0 && (
              <p className="text-xs text-[#94A3B8] mt-3">
                No saved characters yet—add one in the builder, then select them here as narrator.
              </p>
            )}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleCreate}
            disabled={loading}
            data-testid="generate-story-btn"
            className="w-full h-14 rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-accent text-lg shadow-xl shadow-[#6366F1]/25 hover:scale-[1.02] active:scale-95 transition-all"
          >
            {loading ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full" />
            ) : (
              <><TwinneeLogo className="w-5 h-5 mr-2" /> Generate Story <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>
        </div>
      </motion.div>
      </div>

      {/* Expandable right panel: Dialogue stories — below Navbar (h-16 z-50) so the toggle is clickable */}
      <aside
        className={`fixed top-16 right-0 z-40 flex h-[calc(100vh-4rem)] flex-col border-l border-slate-200 bg-white shadow-[0_0_40px_-10px_rgba(0,0,0,0.12)] transition-[width,transform] duration-200 ease-out ${
          dialoguePanelOpen ? 'w-full sm:w-80' : 'w-11'
        }`}
        aria-label="Dialogue stories library panel"
        data-testid="story-setup-dialogue-panel"
      >
        <button
          type="button"
          onClick={() => setDialoguePanelOpen((o) => !o)}
          className="flex h-14 shrink-0 items-center justify-center gap-0 border-b border-slate-100 bg-[#FAFBFC] px-1 text-[#475569] hover:bg-[#F1F5F9] hover:text-[#4338CA] transition-colors"
          title={dialoguePanelOpen ? 'Collapse panel' : 'Open dialogue stories'}
          data-testid="story-setup-dialogue-panel-toggle"
        >
          {dialoguePanelOpen ? (
            <ChevronRight className="h-5 w-5 shrink-0" />
          ) : (
            <ChevronLeft className="h-5 w-5 shrink-0" />
          )}
        </button>
        {dialoguePanelOpen ? (
          <>
            <div className="border-b border-slate-100 px-3 py-3">
              <div className="flex items-center gap-2 text-sm font-bold text-[#1E293B]">
                <Library className="h-4 w-4 text-[#6366F1] shrink-0" />
                Dialogue stories
              </div>
              <p className="mt-1 text-[11px] leading-snug text-[#64748B]">
                Multi-character dialogue stories in your library. Click one to fill this form (drafts stay linked for Save draft).
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 h-8 w-full rounded-lg text-xs text-[#6366F1]"
                onClick={loadDialogueStoriesForPanel}
                disabled={loadingDialogueList}
              >
                {loadingDialogueList ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Refresh list'}
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {loadingDialogueList && dialogueStoriesList.length === 0 ? (
                <div className="flex justify-center py-8 text-[#94A3B8]">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : dialogueStoriesList.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4 text-center text-xs text-[#64748B]">
                  No dialogue stories yet. Set story type to “Dialogue story…” and save or generate one—they’ll appear here.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {dialogueStoriesList.map((s) => {
                    const st = (s.status || 'draft').toLowerCase();
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => loadStoryIntoForm(s)}
                          disabled={loadingImportedStoryId === s.id}
                          className="w-full rounded-xl border border-slate-100 bg-white px-3 py-2.5 text-left text-sm transition-colors hover:border-[#C7D2FE] hover:bg-[#F8FAFF] disabled:opacity-60"
                          data-testid={`story-setup-load-dialogue-${s.id}`}
                        >
                          <span className="line-clamp-2 font-medium text-[#1E293B]">{s.title || 'Untitled'}</span>
                          <span className="mt-1 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide text-[#94A3B8]">
                            <span>{st}</span>
                            {s.scene_count != null ? <span>{s.scene_count} scenes</span> : null}
                          </span>
                          {loadingImportedStoryId === s.id ? (
                            <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#6366F1]">
                              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center pt-4 pb-2 px-0">
            <Library className="h-5 w-5 text-[#6366F1] shrink-0" />
            <span
              className="mt-3 block max-w-[2.5rem] text-center text-[10px] font-semibold leading-tight text-[#64748B]"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              Dialogue stories
            </span>
          </div>
        )}
      </aside>

      <Dialog
        open={charProfileOpen}
        onOpenChange={(open) => {
          setCharProfileOpen(open);
          if (!open) {
            setCharProfileId(null);
            setCharProfileJson('');
            setCharProfileSnapshotJson(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-[min(94vw,56rem)] overflow-y-auto sm:max-w-[min(94vw,56rem)]">
          <DialogHeader>
            <DialogTitle>Creative profile — {charProfileName}</DialogTitle>
            <DialogDescription>
              Full character analysis JSON (editable). Use Reset to restore the last AI detection snapshot when available.
              Upload a portrait — it saves as the library reference image and fills{' '}
              <span className="font-mono text-xs">character.referenceFrames.frontPortrait</span>. Runway turnaround adds
              eight angles under <span className="font-mono text-xs">faceFront … bodyRight</span> (uploaded portrait stays
              optional and is reused as Runway reference when present).
            </DialogDescription>
          </DialogHeader>
          <input
            ref={charPortraitFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onPortraitFileSelected}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              disabled={!charProfileId || charPhotoUploading || charPhotoRemoving}
              onClick={() => charPortraitFileRef.current?.click()}
            >
              {charPhotoUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="h-4 w-4" />
              )}
              Upload portrait
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg gap-2 text-red-600 hover:text-red-700"
              disabled={!charProfileId || !creativeProfilePortraitPreviewSrc || charPhotoUploading || charPhotoRemoving}
              onClick={removePortrait}
              data-testid="story-setup-remove-portrait"
            >
              {charPhotoRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Remove portrait
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={resetCreativeProfileToSnapshot}>
              Reset to AI snapshot
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg gap-1.5"
              disabled={!charProfileId}
              onClick={previewCharacterReferenceSheet}
              data-testid="story-setup-ref-sheet-preview"
            >
              <LayoutGrid className="h-4 w-4" />
              Preview sheet JSON
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="rounded-lg gap-1.5"
              disabled={!charProfileId || refSheetBusy}
              onClick={generateRunwayReferenceSheet}
              data-testid="story-setup-ref-sheet-runway"
            >
              {refSheetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <TwinneeLogo className="h-4 w-4" />}
              Generate 8 views (Runway)
            </Button>
          </div>
          {creativeProfilePortraitPreviewSrc ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-3">
              <p className="mb-2 text-xs font-semibold text-[#475569]">Uploaded portrait preview</p>
              <div className="overflow-hidden rounded-lg border border-white bg-white">
                <img
                  src={creativeProfilePortraitPreviewSrc}
                  alt={`${charProfileName || 'Character'} portrait`}
                  className="h-48 w-full object-contain"
                />
              </div>
            </div>
          ) : null}
          <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-3">
            <p className="mb-2 text-xs font-semibold text-[#475569]">
              Eight-angle preview <span className="font-normal text-[#94A3B8]">(paths from JSON below)</span>
            </p>
            <p className="mb-3 text-[11px] leading-relaxed text-[#64748B]">
              Consistency seed (stored under{' '}
              <span className="font-mono text-[10px]">character.referenceGeneration.seed</span> and{' '}
              <span className="font-mono text-[10px]">reference_sheet_seed</span>):
              {' '}
              <span className="font-semibold text-[#334155]">
                {storySetupStoredSeedDisplay != null && storySetupStoredSeedDisplay !== ''
                  ? String(storySetupStoredSeedDisplay)
                  : '—'}
              </span>{' '}
              Bulk flow: <span className="font-semibold">faceFront</span> runs first (portrait refs if uploaded), then
              face/back/left/right + <span className="font-semibold">bodyFront</span> use that face front, then the three{' '}
              <span className="font-semibold">body*</span> views use body front — same seed everywhere. Individual
              regen mirrors that (strict <span className="font-mono text-[10px]">referenceFrames.faceFront</span> /{' '}
              <span className="font-mono text-[10px]">bodyFront</span> paths).
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {CHARACTER_REFERENCE_GRID.map(({ key, label }) => {
                const raw = creativeProfileReferenceFrames[key];
                const src =
                  typeof raw === 'string' && raw.startsWith('/api/')
                    ? `${BACKEND_URL}${raw}`
                    : typeof raw === 'string' && (raw.startsWith('http') || raw.startsWith('data:'))
                      ? raw
                      : null;
                const usesFaceAnchor = ['faceBack', 'faceLeft', 'faceRight', 'bodyFront'].includes(key);
                const usesBodyAnchor = ['bodyBack', 'bodyLeft', 'bodyRight'].includes(key);
                const prereqBlocked =
                  (usesFaceAnchor && !referenceSheetRegenDeps.faceFrontSheet) ||
                  (usesBodyAnchor && !referenceSheetRegenDeps.bodyFrontSheet);
                const regenBusy = regeneratingAngleKey === key;
                const regenTitle = usesFaceAnchor
                  ? !referenceSheetRegenDeps.faceFrontSheet
                    ? 'Save a faceFront asset first (/api/media/… in JSON)'
                    : 'Regenerate with Runway using faceFront as reference'
                  : usesBodyAnchor
                    ? !referenceSheetRegenDeps.bodyFrontSheet
                      ? 'Save a bodyFront asset first'
                      : 'Regenerate with Runway using bodyFront as reference'
                    : 'Regenerate faceFront (optional portrait refs)';
                return (
                  <div key={key} className="overflow-hidden rounded-lg border border-white bg-white shadow-sm">
                    <div className="relative aspect-square bg-slate-100">
                      {src ? (
                        <img src={src} alt={label} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center p-2 text-center text-[10px] text-[#94A3B8]">
                          —
                        </div>
                      )}
                      <div className="absolute bottom-1 right-1 flex gap-1">
                        <button
                          type="button"
                          title={regenTitle}
                          disabled={!!refSheetBusy || regenBusy || prereqBlocked}
                          onClick={() => regenerateRunwayAngle(key)}
                          data-testid={`story-setup-ref-regen-${key}`}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-white/80 bg-black/55 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-black/65 disabled:pointer-events-none disabled:opacity-40"
                        >
                          {regenBusy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                          )}
                          <span className="sr-only">Regenerate {key}</span>
                        </button>
                      </div>
                    </div>
                    <div className="truncate px-1.5 py-1 text-center font-mono text-[9px] text-[#64748B]" title={key}>
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-4">
            <p className="mb-2 text-xs font-semibold text-[#5B21B6]">Runway Live avatar</p>
            <p className="mb-3 text-[11px] leading-relaxed text-[#64748B]">
              After <span className="font-semibold text-[#4338CA]">faceFront</span> is generated and saved (see grid above),
              you can call Runway&apos;s{' '}
              <span className="font-mono text-[10px]">POST /v1/avatars</span> with that image as{' '}
              <span className="font-mono text-[10px]">referenceImage</span>. Requires a public HTTPS URL (signed S3 from this
              app). Uses your character fields for <span className="font-mono text-[10px]">personality</span> unless you
              override later via API.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="min-w-[12rem] flex-1">
                <Label className="mb-1.5 block text-xs font-medium text-[#475569]">Preset voice</Label>
                <Select value={runwayAvatarVoice} onValueChange={setRunwayAvatarVoice} disabled={runwayAvatarBusy}>
                  <SelectTrigger
                    className="h-10 rounded-lg border-2 border-slate-200 bg-white"
                    data-testid="story-setup-runway-avatar-voice"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[min(60vh,20rem)]">
                    {RUNWAY_LIVE_AVATAR_PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="secondary"
                className="rounded-lg gap-2 bg-violet-600 text-white hover:bg-violet-700"
                disabled={
                  !charProfileId ||
                  !referenceSheetRegenDeps.faceFrontSheet ||
                  runwayAvatarBusy ||
                  refSheetBusy ||
                  !!regeneratingAngleKey
                }
                onClick={createRunwayLiveAvatar}
                data-testid="story-setup-runway-live-avatar-btn"
              >
                {runwayAvatarBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <TwinneeLogo className="h-4 w-4" />}
                {runwayAvatarBusy ? 'Creating avatar…' : 'Create Runway Live avatar'}
              </Button>
            </div>
            {profileCharRunwayLiveAvatar?.id ? (
              <p className="mt-3 font-mono text-[10px] text-[#64748B]">
                Last avatar id: {String(profileCharRunwayLiveAvatar.id)}
                {profileCharRunwayLiveAvatar.status != null
                  ? ` · status: ${String(profileCharRunwayLiveAvatar.status)}`
                  : ''}
              </p>
            ) : null}
          </div>
          <textarea
            className="min-h-[22rem] w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed"
            spellCheck={false}
            value={charProfileJson}
            onChange={(e) => setCharProfileJson(e.target.value)}
            aria-label="Creative profile JSON"
            data-testid="story-setup-creative-profile-json"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCharProfileOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={saveCreativeProfile} disabled={charProfileSaving}>
              {charProfileSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refSheetPreviewOpen} onOpenChange={setRefSheetPreviewOpen}>
        <DialogContent className="max-h-[90vh] max-w-[min(94vw,40rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Character reference sheet (preview)</DialogTitle>
            <DialogDescription>
              JSON built from name, description, traits, and <span className="font-mono text-xs">creative_profile.character</span>.
              Saving is not applied until you generate with Runway or edit the creative profile manually.
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-lg border bg-slate-950 p-3 text-[11px] text-slate-100">
            {refSheetPreviewText}
          </pre>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRefSheetPreviewOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
